import { useEffect, useState } from "react";
import api from "@/lib/api";
import { X, Mail, Phone, MapPin, Calendar, CheckCircle2, XCircle, Clock, Trash2 } from "lucide-react";

export default function ApplicationDetailModal({ kind, id, onClose, onUpdated }) {
  const [data, setData] = useState(null);
  const [working, setWorking] = useState(false);

  useEffect(() => {
    if (!id) return;
    api.get(`/admin/applications/${kind}/${id}`).then((r) => setData(r.data)).catch(() => setData(false));
  }, [kind, id]);

  const setStatus = async (status) => {
    setWorking(true);
    try {
      const url = kind === "membership"
        ? `/memberships/${id}/status?status=${status}`
        : `/admin/applications/affiliation/${id}/status?status=${status}`;
      await api.patch(url);
      setData((d) => ({ ...d, status }));
      onUpdated && onUpdated();
    } finally { setWorking(false); }
  };

  const remove = async () => {
    if (!window.confirm(`Permanently delete this ${kind} application? This cannot be undone.`)) return;
    setWorking(true);
    try {
      const url = kind === "membership" ? `/memberships/${id}` : `/affiliations/${id}`;
      await api.delete(url);
      onUpdated && onUpdated();
      onClose && onClose();
    } finally { setWorking(false); }
  };

  if (!id) return null;
  return (
    <div onClick={onClose} className="fixed inset-0 z-50 bg-nkf-navy/80 backdrop-blur-sm flex items-center justify-center p-4 sm:p-6" data-testid="app-detail-modal">
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-md max-w-2xl w-full max-h-[88vh] overflow-y-auto animate-scale-in">
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white z-10">
          <div>
            <div className="text-[11px] uppercase tracking-[0.25em] text-nkf-red font-semibold">{kind === "membership" ? "Membership" : "Affiliation"} Application</div>
            <div className="font-display text-xl text-nkf-blue">{data?.application_no || "—"}</div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-nkf-blue p-1" aria-label="Close"><X className="h-5 w-5" /></button>
        </div>

        {!data && <div className="p-8 text-center text-slate-500">Loading…</div>}
        {data === false && <div className="p-8 text-center text-red-500">Application not found.</div>}

        {data && (
          <div className="p-5 sm:p-6 space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label={kind === "membership" ? "Applicant" : "Organisation"} value={kind === "membership" ? data.applicant_name : data.organisation_name} />
              <Field label={kind === "membership" ? "Organisation" : "Contact Person"} value={kind === "membership" ? (data.organisation_name || "—") : data.contact_person} />
              <Field label="Email" icon={Mail} value={<a href={`mailto:${data.email}`} className="text-nkf-blue hover:underline">{data.email}</a>} />
              <Field label="Phone" icon={Phone} value={<a href={`tel:${data.phone}`} className="text-nkf-blue hover:underline">{data.phone}</a>} />
              <Field label="State" icon={MapPin} value={data.state} />
              <Field label="District" value={data.district} />
              {kind === "membership" && <Field label="Category" value={(data.category || "").replace("_", " ")} />}
              {kind === "membership" && <Field label="Discipline" value={(data.discipline || "").replace("_", " ")} />}
              {kind === "membership" && <Field label="Date of Birth" value={data.dob || "—"} />}
              {kind === "membership" && <Field label="Gender" value={data.gender} />}
              {kind === "membership" && <Field label="Belt Rank" value={data.belt_rank || "—"} />}
              {kind === "affiliation" && <Field label="Type" value={data.type} />}
              {kind === "affiliation" && <Field label="Established" value={data.established_year || "—"} />}
              <Field label="Submitted" icon={Calendar} value={(data.submitted_at || "").slice(0, 16).replace("T", " ")} wide />
              {data.address && <Field label="Address" value={data.address} wide />}
              {data.notes && <Field label="Notes" value={data.notes} wide />}
            </div>

            <div className="border-t border-slate-200 pt-4 flex flex-wrap items-center justify-between gap-3">
              <div className="text-sm">
                Current status: <Badge status={data.status} />
              </div>
              <div className="flex flex-wrap gap-2">
                <button disabled={working} onClick={() => setStatus("approved")} data-testid="approve-btn" className="inline-flex items-center gap-1 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold uppercase tracking-widest px-3 py-2 rounded transition disabled:opacity-60">
                  <CheckCircle2 className="h-3.5 w-3.5" /> Approve
                </button>
                <button disabled={working} onClick={() => setStatus("pending")} className="inline-flex items-center gap-1 bg-amber-500 hover:bg-amber-600 text-white text-xs font-bold uppercase tracking-widest px-3 py-2 rounded transition disabled:opacity-60">
                  <Clock className="h-3.5 w-3.5" /> Mark Pending
                </button>
                <button disabled={working} onClick={() => setStatus("rejected")} data-testid="reject-btn" className="inline-flex items-center gap-1 bg-red-600 hover:bg-red-700 text-white text-xs font-bold uppercase tracking-widest px-3 py-2 rounded transition disabled:opacity-60">
                  <XCircle className="h-3.5 w-3.5" /> Reject
                </button>
                <button disabled={working} onClick={remove} data-testid="delete-btn" className="inline-flex items-center gap-1 bg-slate-800 hover:bg-black text-white text-xs font-bold uppercase tracking-widest px-3 py-2 rounded transition disabled:opacity-60">
                  <Trash2 className="h-3.5 w-3.5" /> Delete
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ label, value, icon: Icon, wide }) {
  return (
    <div className={`${wide ? "sm:col-span-2" : ""}`}>
      <div className="text-[10.5px] uppercase tracking-widest text-slate-500 font-semibold flex items-center gap-1.5">
        {Icon && <Icon className="h-3 w-3" />} {label}
      </div>
      <div className="text-sm text-nkf-blue font-medium mt-0.5 break-words">{value || "—"}</div>
    </div>
  );
}
function Badge({ status }) {
  const c = status === "approved" ? "bg-emerald-100 text-emerald-700" : status === "rejected" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-800";
  return <span className={`text-[10.5px] uppercase tracking-widest font-bold px-2 py-1 rounded ${c}`}>{status || "pending"}</span>;
}
